import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {NodeSpec} from '../execution/graph';
import {Source} from '../execution/pipes';
import {ArrayValue, PlatedMaterialValue} from '../types/data';

import {NODE as NODE_BREAKOUT_MATERIALS} from './breakout_materials_node';
import {NODE as NODE_MAGNETIC_BEAD_WASH} from './magnetic_bead_wash_node';
import {NODE as NODE_MIX_ARRAY} from './mix_array_node';
import {NODE as NODE_MIX_PRODUCT} from './mix_product_node';
import {NODE as NODE_MIX_ZIPPER} from './mix_zipper_node';
import {MixingTransformer} from './mixing_transformer';
import {OutputMaterialsSink} from './output_materials_sink';
import {NODE as NODE_PLATED_MATERIAL} from './plated_material_node';
import {NODE as NODE_SERIAL_DILUTIONS} from './serial_dilutions_node';
import {makeWaterPlatedMaterial} from './sources';
import {NODE_SUBGRAPH_INPUT, NODE_SUBGRAPH_OUTPUT} from './subgraph_nodes';
import {NODE as NODE_THERMOCYCLE} from './thermocycling_node';

export const NODE_MIX_TABLE = {
  id: 'node_mix',
  label: 'Mix table',
  inputs: [
    {
      id: 'materials',
      label: 'Sources',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'mixes',
      label: 'Mixes',
      type: {
        kind: 'table',
        columnLabels: ['Label'],
        columnTypes: [{kind: 'string'}],
        defaults: [{kind: 'string', value: ''}],
        rowCountFixed: false,
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'remainder',
      label: 'Remainder',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    );
    const mixes = overlay.singleValuePadSource(
      checkExists(node.inputs.get('mixes')),
      'table',
      () => ({
        kind: 'table',
        values: new Map([['Label', []]]),
      }),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const remainder = overlay.singleValuePadSource(
      checkExists(node.inputs.get('remainder')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );

    const mix = new MixingTransformer(
      overlay.nextIdFor(node),
      materials as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      mixes,
      plate,
      remainder,
    );
    overlay.setPadSource(checkExists(node.outputs.get('materials')), mix);
  },
} as const satisfies NodeSpec;

export const NODE_STORE_MATERIALS = {
  id: 'node_store_materials',
  label: 'Store materials',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'volume',
      label: 'Volume',
      type: {
        kind: 'volume',
        minimumNl: 0,
        maximumNl: undefined,
      },
    },
  ],
  outputs: [],
  pipe: ({node, overlay}) => {
    const volume = overlay.singleValuePadSource(
      checkExists(node.inputs.get('volume')),
      'volume',
      () => ({
        kind: 'volume',
        numberNl: 10_000,
        preferredUnit: 'ul',
      }),
    );
    const inputs = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    );
    new OutputMaterialsSink(overlay.nextIdFor(node), volume, inputs);
  },
} as const satisfies NodeSpec;

export const NODE_SPECS = [
  NODE_BREAKOUT_MATERIALS,
  NODE_MAGNETIC_BEAD_WASH,
  NODE_MIX_ARRAY,
  NODE_MIX_PRODUCT,
  NODE_MIX_TABLE,
  NODE_MIX_ZIPPER,
  NODE_PLATED_MATERIAL,
  NODE_SERIAL_DILUTIONS,
  NODE_STORE_MATERIALS,
  NODE_SUBGRAPH_INPUT,
  NODE_SUBGRAPH_OUTPUT,
  NODE_THERMOCYCLE,
];
